import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

const NetworkErrorContext = createContext();

export const useNetworkError = () => {
  const context = useContext(NetworkErrorContext);
  if (!context) {
    throw new Error(
      "useNetworkError must be used within a NetworkErrorProvider",
    );
  }
  return context;
};

export const NetworkErrorProvider = ({ children }) => {
  const [isOffline, setIsOffline] = useState(false);
  const [networkError, setNetworkError] = useState(null);
  const [errorCount, setErrorCount] = useState(0);
  const [lastErrorTime, setLastErrorTime] = useState(0);

  // Debounce period in milliseconds to prevent rapid duplicate errors
  const ERROR_DEBOUNCE_MS = 1000;

  const showNetworkError = useCallback(
    (
      errorMessage = "Network error. Please check your internet connection.",
    ) => {
      const now = Date.now();

      // Only show modal if actually offline (no internet), not just slow connection
      if (navigator.onLine) {
        return;
      }

      // Debounce: Only show error if enough time has passed since the last error
      if (now - lastErrorTime < ERROR_DEBOUNCE_MS) {
        return;
      }

      setNetworkError(errorMessage);
      setIsOffline(true);
      setErrorCount((prev) => prev + 1);
      setLastErrorTime(now);
    },
    [lastErrorTime],
  );

  const hideNetworkError = useCallback(() => {
    setNetworkError(null);
    setIsOffline(false);
  }, []);

  const clearNetworkError = useCallback(() => {
    setNetworkError(null);
    setIsOffline(false);
    setErrorCount(0);
  }, []);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setNetworkError(null);
    };

    const handleOffline = () => {
      setIsOffline(true);
      showNetworkError(
        "You are currently offline. Please check your internet connection.",
      );
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Check initial online status
    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [showNetworkError]);

  const value = {
    isOffline,
    networkError,
    errorCount,
    showNetworkError,
    hideNetworkError,
    clearNetworkError,
  };

  return (
    <NetworkErrorContext.Provider value={value}>
      {children}
    </NetworkErrorContext.Provider>
  );
};
